import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Modal from 'components/Modal';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import { useAppContext } from 'contexts/AppContext';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import ALL_ROUTES from 'config/routes';

import './styles.scss';
import PropTypes from 'prop-types';
import { toastStyle } from 'utils/helper';
import {
  buildBudgetTemplateOptions,
  getUserBudgetTemplates,
  isUserTemplateOption,
} from 'utils/budgetTemplates';
import {
  currencyOptionFor,
  currencyOptions,
  normalizeCurrencyCode,
} from 'utils/currencies';

const CreateBudgetInsideFolder = props => {
  const navigate = useNavigate();
  const { userData, userProfile } = useAppContext();
  const {
    createBudgetMutation,
    duplicateBudgetMutation,
    refetchBudgets,
    budgetList,
    budgetTypes,
    hideCreateBudgetModal,
    renameBudget,
    selectTab,
    clearBudgetRename,
    refetchBudgetId,
    openCreatedBudget,
  } = useHomeScreenContext();

  const userTemplates = useMemo(
    () => getUserBudgetTemplates(budgetList, userData?.email),
    [budgetList, userData?.email]
  );
  const budgetTypeOptions = useMemo(
    () =>
      buildBudgetTemplateOptions(
        budgetTypes,
        renameBudget?.id ? [] : userTemplates
      ),
    [budgetTypes, renameBudget?.id, userTemplates]
  );
  const defaultCurrency = normalizeCurrencyCode(
    renameBudget?.primary_currency || userProfile?.userprofile?.default_currency
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      name: renameBudget?.name || '',
      template_type: renameBudget?.template_type?.toString() || '',
      primary_currency: currencyOptionFor(defaultCurrency),
      folder: props?.parentid,
    },
  });

  const handleCreateBudget = () =>
    handleSubmit(async data => {
      try {
        const selectedTemplate = data?.template_type;
        const primaryCurrency = normalizeCurrencyCode(
          data?.primary_currency?.value || data?.primary_currency,
          defaultCurrency
        );
        const response =
          !renameBudget?.id && isUserTemplateOption(selectedTemplate)
            ? await duplicateBudgetMutation.mutateAsync({
                budget_id: selectedTemplate?.budget_id,
                withValues: true,
                name: data?.name,
                primary_currency: primaryCurrency,
                is_user_template: false,
                copy_folders: false,
                target_folder_id: props?.parentid,
              })
            : await createBudgetMutation.mutateAsync({
                ...data,
                template_type: selectedTemplate?.value,
                primary_currency: primaryCurrency,
                id: renameBudget?.id,
                folder: props?.parentid,
              });
        const newBudget = isUserTemplateOption(selectedTemplate)
          ? response?.data?.data
          : response?.data;

        if (renameBudget?.id) {
          newBudget?.id && selectTab(newBudget.id, newBudget);
          refetchBudgets?.();
          refetchBudgetId?.();
          toast.success(
            `Budget "${data?.name}" updated successfully`,
            toastStyle
          );
          handleCancel();
          props?.refetch();
          return;
        }

        if (!newBudget?.id) {
          throw new Error('The created budget response did not include an ID.');
        }

        const createdTab = await openCreatedBudget(newBudget);
        handleCancel();
        if (createdTab) {
          navigate(ALL_ROUTES.HOME_SCREEN);
        } else {
          props?.refetch();
        }
      } catch (error) {
        console.error('Error creating budget inside folder:', error);
        const message =
          error?.response?.data?.template_type?.[0] ||
          error?.response?.data?.name?.[0] ||
          error?.response?.data?.non_field_errors?.[0];
        toast.error(
          message ||
            `An error occurred while ${
              renameBudget?.id ? 'updating' : 'creating'
            } budget. Please try again later`,
          toastStyle
        );
      }
    });

  const handleCancel = () => {
    hideCreateBudgetModal();
    clearBudgetRename();
    reset();
    props?.closeFolder();
  };

  return (
    <Modal
      onHide={handleCancel}
      show={props?.showBudgetFolder}
      size="sm"
      className="create-budget"
      bodyClass="create-budget__body"
      centered={true}
    >
      <h1 className="create-budget__body__header">
        {renameBudget?.id ? 'Edit' : 'Create'} Budget{' '}
        {watch('name') && (
          <span className="budget-name">&ldquo;{watch('name')}&rdquo;</span>
        )}
      </h1>
      <Form
        className="create-budget__body__form"
        onSubmit={handleCreateBudget()}
        aria-busy={isSubmitting}
      >
        <FormInput
          label="Budget Title"
          type="text"
          error={errors?.name?.message}
          placeholder="Please enter a budget title"
          autoFocus
          {...register('name', {
            required: 'Budget title is required',
          })}
        />
        <FormSelect
          label="Budget Template"
          placeholder="Please select a Template"
          options={budgetTypeOptions}
          error={errors?.template_type?.message}
          name="template_type"
          rules={{
            required: false,
          }}
          control={control}
        />
        <FormSelect
          label="Primary Currency"
          placeholder="Select a currency"
          options={currencyOptions}
          error={errors?.primary_currency?.message}
          name="primary_currency"
          rules={{
            required: false,
          }}
          control={control}
          isSearchable
        />
        <div className="create-budget__body__action">
          <Button
            label="Cancel"
            type="reset"
            className="create-budget__action__cancel"
            variant="red-outlined"
            onClick={handleCancel}
            isDisabled={isSubmitting}
          />
          <Button
            label={isSubmitting ? 'Creating...' : 'Create'}
            className="create-budget__action__confirm"
            type="submit"
            isDisabled={isSubmitting}
          />
        </div>
      </Form>
    </Modal>
  );
};

CreateBudgetInsideFolder.propTypes = {
  parentid: PropTypes.string, // Assuming parentid is a string, adjust the type accordingly
  showBudgetFolder: PropTypes.bool,
  closeFolder: PropTypes.func,
  refetch: PropTypes.func.isRequired, // Adjust the type based on the actual type of refetch
};

CreateBudgetInsideFolder.defaultProps = {
  parentid: '', // Default value for parentid (adjust based on actual default)
  showBudgetFolder: false, // Default value for showAddFolder (adjust based on actual default)
  closeFolder: undefined,
};

export default CreateBudgetInsideFolder;
